import * as Sentry from "@sentry/react";

export interface ErrorContext {
  tags?: Tags;
  extras?: Extras;
}

type Extras = Record<string, unknown>;

type Tags = Record<string, Primitive>;

type Primitive = number | string | boolean | bigint | symbol | null | undefined;

type LogEntry = string | Error;

export const isLogEntryError = (error: unknown): error is LogEntry =>
  error instanceof Error || typeof error === "string";

export class Log {
  public static debug(entry: LogEntry, errorContext?: ErrorContext): void {
    Log.log(entry, "debug", errorContext);
  }

  public static info(entry: LogEntry, errorContext?: ErrorContext): void {
    Log.log(entry, "info", errorContext);
  }

  public static warn(entry: LogEntry, errorContext?: ErrorContext): void {
    Log.log(entry, "warning", errorContext);
  }

  public static error(error: LogEntry, errorContext?: ErrorContext): void {
    Log.log(error, "error", errorContext);
  }

  public static fatal(error: Error, errorContext?: ErrorContext): void {
    Log.log(error, "fatal", errorContext);
  }

  public static log(
    error: LogEntry,
    severity: Sentry.SeverityLevel,
    context?: ErrorContext,
  ): void {
    const capture = (err: LogEntry): void | string => {
      return typeof err === "string"
        ? Sentry.captureMessage(err, severity)
        : Sentry.captureException(err);
    };

    if (Log.shouldLogToSentry(severity)) {
      Sentry.withScope((scope) => {
        if (context) {
          if (context.extras) {
            scope.setExtras(context.extras);
          }
          if (context.tags) {
            scope.setTags(context.tags);
          }
        }
        capture(error);
      });
    }
  }

  public static shouldLogToSentry(severity: Sentry.SeverityLevel): boolean {
    return (
      Log.mapToSeverityLevel(severity) <=
      Log.mapToSeverityLevel(window.env.SENTRY_LOG_LEVEL)
    );
  }

  public static mapToSeverityLevel(severity: string): number {
    switch (severity) {
      case "debug":
        return 5;
      case "info":
        return 4;
      case "warning":
        return 3;
      case "error":
        return 2;
      case "fatal":
        return 1;
      default:
        return 2;
    }
  }
}
